import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Cpu, Database, Activity, XCircle, RefreshCw, Search, Loader2, AlertTriangle } from 'lucide-react';

const TaskManagerTab = ({ agentId }) => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('cpu');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [killLoading, setKillLoading] = useState({});

  useEffect(() => {
    fetchProcesses();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchProcesses, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [agentId, autoRefresh]);

  const fetchProcesses = async () => {
    try {
      const res = await api.get(`/agents/${agentId}/processes`);
      setProcesses(res.data.processes || []);
    } catch (error) {
      toast.error('Failed to load processes');
    } finally {
      setLoading(false);
    }
  };

  const handleKillProcess = async (pid, name) => {
    if (!window.confirm(`Are you sure you want to kill process "${name}" (PID: ${pid})?`)) {
      return;
    }

    setKillLoading(prev => ({ ...prev, [pid]: true }));

    try {
      const response = await api.post(`/agents/${agentId}/processes/${pid}/kill`);
      const data = response.data;
      
      if (data.immediate) {
        // Command was executed immediately via WebSocket
        if (data.status === 'completed') {
          toast.success(`Process "${name}" (PID: ${pid}) killed successfully`);
          // Refresh immediately since process should be gone
          setTimeout(fetchProcesses, 500);
        } else if (data.status === 'failed') {
          toast.error(data.message || `Failed to kill process "${name}"`);
        }
      } else {
        // Command was queued - agent will pick it up on next check-in
        toast.warning(`Kill command queued for "${name}" - will execute on next agent check-in`, {
          duration: 5000
        });
        // Refresh after a delay
        setTimeout(fetchProcesses, 3000);
      }
    } catch (error) {
      toast.error('Failed to send kill command');
    } finally {
      setKillLoading(prev => ({ ...prev, [pid]: false }));
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const filteredProcesses = processes
    .filter(p => 
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.pid?.toString().includes(search)
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'cpu':
          return (b.cpu_percent || 0) - (a.cpu_percent || 0);
        case 'memory':
          return (b.memory_mb || 0) - (a.memory_mb || 0);
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

  const totalCpu = processes.reduce((sum, p) => sum + (p.cpu_percent || 0), 0);
  const totalMemory = processes.reduce((sum, p) => sum + (p.memory_mb || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Processes</CardTitle>
            <Activity className="w-4 h-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{processes.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total CPU Usage</CardTitle>
            <Cpu className="w-4 h-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCpu.toFixed(1)}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Memory</CardTitle>
            <Database className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(totalMemory * 1024 * 1024)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search processes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cpu">Sort by CPU</SelectItem>
            <SelectItem value="memory">Sort by Memory</SelectItem>
            <SelectItem value="name">Sort by Name</SelectItem>
          </SelectContent>
        </Select>

        <Button
          onClick={() => setAutoRefresh(!autoRefresh)}
          variant={autoRefresh ? "default" : "outline"}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
          {autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
        </Button>

        <Button onClick={fetchProcesses} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Process List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Running Processes ({filteredProcesses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">PID</th>
                  <th className="text-left py-3 px-4 font-medium">Process Name</th>
                  <th className="text-left py-3 px-4 font-medium">CPU %</th>
                  <th className="text-left py-3 px-4 font-medium">Memory</th>
                  <th className="text-left py-3 px-4 font-medium">Threads</th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProcesses.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">
                      No processes found
                    </td>
                  </tr>
                ) : (
                  filteredProcesses.map((process) => (
                    <tr key={process.pid} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{process.pid}</td>
                      <td className="py-3 px-4 font-medium">
                        <div className="flex items-center gap-2">
                          {process.name}
                          {process.cpu_percent > 50 && (
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={Math.min(process.cpu_percent || 0, 100)} 
                            className="w-20"
                          />
                          <span className="text-sm font-medium">
                            {(process.cpu_percent || 0).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {formatBytes((process.memory_mb || 0) * 1024 * 1024)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {process.threads || 0}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleKillProcess(process.pid, process.name)}
                            disabled={killLoading[process.pid]}
                            className="gap-1"
                          >
                            {killLoading[process.pid] ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <XCircle className="w-3 h-3" />
                            )}
                            Kill
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskManagerTab;
